import * as React from "react";
import {
  GridColDef,
  GridRowsProp,
  GridSortModel,
  GridRowClassNameParams,
  GridValueGetterParams,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import {
  Box,
  Grid,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import NoResultsOverlay from "../../components/NoResultOverlay";
import AddIcon from "@mui/icons-material/Add";
import { Download as DownloadIcon } from "@mui/icons-material";
import useDebounce from "../../hooks/useDebounce";
import { useTitle } from "../../hooks/useTitle";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetBalanceHistoriesQuery } from "../../services/finance.service";
import { BalanceHistory, ManualDeposit } from "../../types/finance";
import dayjs from "dayjs";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import ModalManualDepositForm from "./ModalManualDepositForm";
import { formatMoney } from "../../utils/helper";
import CompanyDrawer from "../company/CompanyDrawer";
import TalentDrawer from "../talent/TalentDrawer";
import { PersonOutline } from "@mui/icons-material";
import ProjectDrawer from "../project/ProjectDrawer";
import { CardBase, GlassDataGrid, StyledTextField, Button, OrangeLinearProgress } from "../../components/global";

const FinancesPage = () => {
  const { pathname } = useLocation();
  const edition = pathname.includes("/home-edition") ? "home-edition" : "workz360";
  const companyType: "Workz360" | "HomeEdition" =
    edition === "home-edition" ? "HomeEdition" : "Workz360";
  useTitle(
    edition === "workz360" ? "General Finances - Workz360" : "General Finances - Home Edition"
  );
  const navigate = useNavigate();
  const user = useCurrentUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formModal, setFormModal] = React.useState<{
    open: boolean;
    data: ManualDeposit | null;
  }>({
    open: false,
    data: null,
  });

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    setSearchQuery(debounceQuery);
  }, [debounceQuery]);

  // for drawers
  const [openCompany, setOpenCompany] = React.useState(false);
  const [currentCompany, setCurrentCompany] = React.useState(null);
  const [openTalent, setOpenTalent] = React.useState(false);
  const [currentTalent, setCurrentTalent] = React.useState(null);
  const [openProject, setOpenProject] = React.useState(false);
  const [currentProject, setCurrentProject] = React.useState(null);

  const onViewCompany = (item: any) => {
    setCurrentCompany(item);
    setOpenCompany(true);
  };

  const onViewTalent = (item: any) => {
    setCurrentTalent(item);
    setOpenTalent(true);
  };

  const onViewProject = (item: any) => {
    setCurrentProject(item);
    setOpenProject(true);
  };

  const { data, isLoading, isFetching } = useGetBalanceHistoriesQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
    companyType,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "created_at",
        headerName: "Time",
        minWidth: isMobile ? 120 : 200,
        sortingOrder: ["desc", "asc", null],
        sortable: true,
        filterable: true,
        valueGetter: (
          params: GridValueGetterParams<undefined, BalanceHistory>
        ) => `${dayjs(params.row?.created_at).format("MMM DD, YYYY hh:mm A")}`,
      },
      {
        field: "name",
        headerName: "Client / Talent",
        minWidth: isMobile ? 150 : 200,
        // sortable: true,
        // filterable: true,
        valueGetter: (
          params: GridValueGetterParams<undefined, BalanceHistory>
        ) => {
          if (!!params.row?.model) {
            return params.row?.model_type === "company"
              ? `${params.row?.model?.name || ""}`
              : `${params.row?.model?.first_name || ""} ${params.row?.model?.last_name || ""
              }`;
          }

          return `${params.row?.model_type}#${params.row?.model_id}`;
        },
        renderCell: (
          params: GridRenderCellParams<undefined, BalanceHistory>
        ) => (
          <Link
            variant="body2"
            component={"button"}
            onClick={() => {
              if (params.row?.model_type === "company") {
                onViewCompany(params.row?.model);
              } else if (params.row?.model_type === "user") {
                onViewTalent(params.row?.model);
              }
            }}
            sx={{
              alignItems: "center",
              display: "flex",
              color: "#F4A259",
              textDecoration: "none",
              fontWeight: 500,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              "&:hover": {
                textDecoration: "underline",
                color: "#E8944A",
              },
            }}
          >
            {params.row?.model_type === "user" && (
              <PersonOutline fontSize="small" sx={{ mr: 1, fontSize: isMobile ? "0.875rem" : "1rem" }} />
            )}
            {params.value}
          </Link>
        ),
      },
      {
        field: "project",
        headerName: "Job ID",
        minWidth: isMobile ? 80 : 120,
        // sortable: true,
        // filterable: true,
        valueGetter: (
          params: GridValueGetterParams<undefined, BalanceHistory>
        ) => {
          if (!!params.row?.reference_model?.title) {
            // this is project
            return params.row?.reference_model?.id || 0;
          } else if (!!params.row?.reference_model?.project_id) {
            // probably bonus/additional hour/reimbursement
            return params.row?.reference_model?.project_id || 0;
          } else {
            return 0;
          }
        },
        renderCell: (params: GridRenderCellParams<undefined, BalanceHistory>) =>
          !!params.value ? (
            <Link
              variant="body2"
              component={"button"}
              onClick={() => {
                onViewProject({ id: params.value });
              }}
              sx={{
                color: "#F4A259",
                textDecoration: "none",
                fontWeight: 500,
                fontSize: isMobile ? "0.75rem" : "0.875rem",
                "&:hover": {
                  textDecoration: "underline",
                  color: "#E8944A",
                },
              }}
            >
              #{params.value}
            </Link>
          ) : (
            ""
          ),
      },
      // {
      //   field: "model_type",
      //   headerName: "Type",
      //   minWidth: 200,
      //   sortable: true,
      //   filterable: true,
      //   valueGetter: (
      //     params: GridValueGetterParams<undefined, BalanceHistory>
      //   ) => `${params.row?.model_type}`,
      // },
      {
        field: "tx_notes",
        headerName: "Notes",
        flex: 1,
        minWidth: isMobile ? 150 : 300,
        sortable: false,
        filterable: false,
        renderCell: (
          params: GridRenderCellParams<undefined, BalanceHistory>
        ) => (
          <Box>
            <Typography variant="body2" sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>{params.row.notes}</Typography>
            {!!params.row.user_comment && (
              <Typography variant="caption" sx={{ fontStyle: "italic", fontSize: isMobile ? "0.7rem" : "0.75rem" }}>
                Reason: {params.row.user_comment}
              </Typography>
            )}
          </Box>
        ),
        disableExport: true,
      },
      {
        field: "notes",
        headerName: "Notes",
        hide: true,
      },
      {
        field: "debit",
        headerName: "Debit",
        headerAlign: "center",
        minWidth: isMobile ? 80 : 130,
        align: "right",
        type: "number",
        sortable: false,
        filterable: false,
        valueGetter: (
          params: GridValueGetterParams<undefined, BalanceHistory>
        ) => `${formatMoney(params.row.debit)}`,
      },
      {
        field: "credit",
        headerName: "Credit",
        headerAlign: "center",
        minWidth: isMobile ? 80 : 130,
        align: "right",
        type: "number",
        sortable: false,
        filterable: false,
        valueGetter: (
          params: GridValueGetterParams<undefined, BalanceHistory>
        ) => `${formatMoney(params.row.credit)}`,
      },
      {
        field: "balance",
        headerName: "Balance",
        headerAlign: "center",
        minWidth: isMobile ? 80 : 130,
        //flex: 1,
        type: "number",
        sortable: true,
        filterable: true,
        align: "right",
        valueGetter: (
          params: GridValueGetterParams<undefined, BalanceHistory>
        ) => `${formatMoney(params.row.balance)}`,
      },
    ],
    [isMobile]
  );

  const handleCreate = React.useCallback(() => {
    setFormModal({ open: true, data: null });
  }, []);

  const handleExport = React.useCallback(() => {
    if (!rows || rows.length === 0) return;

    // Get visible columns
    const visibleColumns = columns.filter((col) => !col.hide);

    // Create CSV headers
    const headers = visibleColumns.map((col) => `"${col.headerName}"`).join(",");

    // Create CSV rows
    const csvRows = rows.map((row: any) => {
      return visibleColumns
        .map((col) => {
          let value: string = "";
          if (col.valueGetter) {
            const params = { row } as any;
            const result = col.valueGetter(params);
            value = result != null ? String(result) : "";
          } else if (col.renderCell) {
            // For renderCell, try to get raw value
            const rawValue = (row as any)[col.field];
            value = rawValue ? String(rawValue) : "";
          } else {
            const rawValue = (row as any)[col.field];
            value = rawValue ? String(rawValue) : "";
          }
          // Escape quotes and wrap in quotes
          if (value && (value.includes(",") || value.includes('"') || value.includes("\n"))) {
            value = `"${value.replace(/"/g, '""')}"`;
          }
          return value || "";
        })
        .join(",");
    });

    // Combine header and rows
    const csvContent = [headers, ...csvRows].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Finance_Activities_${dayjs().format("YYYY-MM-DD")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [rows, columns]);

  const getRowClassName = (params: GridRowClassNameParams) => {
    return !!params.row.deleted_at ? "deleted" : "";
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item lg={4} md={4} sm={12} xs={12}>
          <CardBase
            title="Manual Deposits"
            useGlassCard={false}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Manage manual deposits and transactions
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "stretch", sm: "flex-end" },
                  width: "100%",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"

                  sx={{
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      borderColor: "#F4A259",
                      color: "#F4A259",
                      "&:hover": {
                        backgroundColor: "rgba(244, 162, 89, 0.08)",
                        borderColor: "#F4A259",
                      },
                    }}
                    onClick={() => navigate("/finances/manual-deposits")}
                    size="small"
                    fullWidth={isMobile}
                  >
                    Browse
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                      backgroundColor: "#F4A259",
                      color: "#ffffff",
                      "&:hover": {
                        backgroundColor: "#E8944A",
                      },
                    }}
                    onClick={handleCreate}
                    size="small"
                    fullWidth={isMobile}
                  >
                    {isMobile ? "Add Deposit" : "Add Manual Deposit"}
                  </Button>
                </Stack>
              </Box>
            </Box>
          </CardBase>
        </Grid>
      </Grid>
      <CardBase
        title="Finance Activities"
        useGlassCard={false}
        sx={{ height: "85vh", p: 0 }}
        headerActions={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems="stretch"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <StyledTextField
              size="small"
              label="Enter to search.."
              value={query || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
              sx={{
                minWidth: { xs: "100%", sm: 250 },
                width: { xs: "100%", sm: "auto" },
              }}
            />
            {!!query && (
              <Button
                variant="text"
                onClick={() => setQuery("")}
                fullWidth={isMobile}
                sx={{
                  color: "#F4A259",
                  "&:hover": {
                    backgroundColor: "rgba(244, 162, 89, 0.08)",
                  },
                }}
              >
                Reset
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={!rows || rows.length === 0}
              size="small"
              fullWidth={isMobile}
              sx={{
                borderColor: "#F4A259",
                color: "#F4A259",
                "&:hover": {
                  backgroundColor: "rgba(244, 162, 89, 0.08)",
                  borderColor: "#F4A259",
                },
                "&:disabled": {
                  borderColor: "rgba(244, 162, 89, 0.5)",
                  color: "rgba(244, 162, 89, 0.5)",
                },
              }}
            >
              {isMobile ? "Export" : "Export CSV"}
            </Button>
          </Stack>
        }
      >
        <GlassDataGrid
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          page={page}
          onPageChange={(newPage: number) => setPage(newPage)}
          pageSize={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageSizeChange={(newSize: number) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
          loading={isLoading || isFetching}
          components={{
            LoadingOverlay: OrangeLinearProgress,
            NoResultsOverlay: NoResultsOverlay,
          }}
          disableSelectionOnClick
          disableColumnFilter
          height={isMobile ? "calc(85vh - 120px)" : "calc(85vh - 80px)"}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "transparent",
              borderTop: "none",
              paddingLeft: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              paddingRight: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#F4A259",
                fontWeight: 600,
                fontSize: isMobile ? "0.75rem" : "0.875rem",
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#F4A259",
                "& .MuiDataGrid-iconButtonContainer": {
                  color: "#F4A259",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#F4A259",
                },
              },
            },
            "& .MuiDataGrid-cell": {
              paddingLeft: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              paddingRight: isMobile ? theme.spacing(0.5) : theme.spacing(3),
              py: isMobile ? 1 : 2,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(244, 162, 89, 0.05)",
            },
            "& .deleted": {
              fontStyle: "italic",
              color: "#aaa",
            },
          }}
          getRowClassName={getRowClassName}
        />
      </CardBase>
      <ModalManualDepositForm
        open={formModal.open}
        onClose={() => setFormModal({ open: false, data: null })}
        data={formModal.data}
      />
      <CompanyDrawer
        data={currentCompany!}
        open={openCompany}
        setOpen={setOpenCompany}
      />
      <TalentDrawer
        data={currentTalent!}
        open={openTalent}
        setOpen={setOpenTalent}
        forceRefresh={true}
      />
      <ProjectDrawer
        data={currentProject!}
        open={openProject}
        setOpen={setOpenProject}
        forceRefresh={true}
      />
    </Box>
  );
};
export default FinancesPage;
